/**
 * Switch toggle. A `role="switch"` button — native keyboard (Space/Enter) works
 * for free; `aria-checked` reflects state. Token-driven, focus-visible ring,
 * reduced-motion honored. Controlled: emits the next value via `onChange`.
 */
import type { JSX } from 'solid-js';
import { ensureBaseStyles } from '../internal/base-styles.ts';
import { cx } from '../internal/cx.ts';
import { ensureStyles } from '../internal/inject-styles.ts';

const STYLE_ID = 'f4-toggle';
const CSS = `
.f4-toggle {
  position: relative;
  display: inline-flex;
  align-items: center;
  width: var(--space-10);
  height: var(--space-6);
  padding: 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-pill);
  background: var(--color-surface-sunken);
  cursor: pointer;
  transition: background-color var(--duration-fast, 150ms) var(--ease-standard, ease);
}
.f4-toggle[aria-checked="true"] { background: var(--color-accent); border-color: var(--color-accent); }
.f4-toggle[disabled] { opacity: 0.6; cursor: not-allowed; }
.f4-toggle__thumb {
  position: absolute;
  left: var(--space-1);
  width: var(--space-4);
  height: var(--space-4);
  border-radius: var(--radius-circle);
  background: var(--color-surface);
  box-shadow: var(--elevation-1);
  transition: transform var(--duration-fast, 150ms) var(--ease-standard, ease);
}
.f4-toggle[aria-checked="true"] .f4-toggle__thumb { transform: translateX(var(--space-4)); }
@media (prefers-reduced-motion: reduce) {
  .f4-toggle, .f4-toggle__thumb { transition: none; }
}
`;

export interface ToggleProps {
  /** Current state. */
  readonly checked: boolean;
  /** Emitted with the next state on activation. */
  readonly onChange: (checked: boolean) => void;
  /** Accessible label (required — no bare literal). */
  readonly label: string;
  readonly disabled?: boolean;
  readonly id?: string;
  readonly class?: string;
}

export function Toggle(props: ToggleProps): JSX.Element {
  ensureBaseStyles();
  ensureStyles(STYLE_ID, CSS);
  return (
    <button
      type="button"
      id={props.id}
      role="switch"
      aria-checked={props.checked ? 'true' : 'false'}
      aria-label={props.label}
      disabled={props.disabled}
      class={cx('f4-toggle', 'f4-focus-ring', props.class)}
      onClick={() => props.onChange(!props.checked)}
    >
      <span class="f4-toggle__thumb" aria-hidden="true" />
    </button>
  );
}
